import "./menuAdmin.css";
import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  fetchEventsForAdmin,
  fetchMenusFromApi,
  saveEventsToApi,
  saveMenusToApi,
} from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";

function getTodayDateKey() {
  const parts = new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    return new Date().toISOString().slice(0, 10);
  }

  return `${year}-${month}-${day}`;
}

function normalizeDateKey(value) {
  const safe = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(safe) ? safe : "";
}

function buildDailyMenuEventId(menuId) {
  return `daily-menu-${menuId}`;
}

function MenuAdmin() {
  const [menus, setMenus] = useState([]);
  const [editingMenu, setEditingMenu] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sauvegarde locale immédiate + persistance API.
  const saveMenus = async (updatedMenus) => {
    setMenus(updatedMenus);
    localStorage.setItem("carpeDiemMenus", JSON.stringify(updatedMenus));

    try {
      const persistedMenus = await saveMenusToApi(updatedMenus);
      if (Array.isArray(persistedMenus) && persistedMenus.length > 0) {
        localStorage.setItem("carpeDiemMenus", JSON.stringify(persistedMenus));
        setMenus(persistedMenus);
      }
      window.dispatchEvent(new Event("carpe-content-changed"));
      return { success: true };
    } catch (_error) {
      return {
        success: false,
        error:
          "La sauvegarde distante a échoué. Les données restent en cache local.",
      };
    }
  };

  // Chargement initial depuis cache local puis API.
  useEffect(() => {
    let isMounted = true;

    const loadMenus = async () => {
      const savedMenus = localStorage.getItem("carpeDiemMenus");
      if (savedMenus) {
        const parsedMenus = safeJsonParse(savedMenus, []);
        setMenus(Array.isArray(parsedMenus) ? parsedMenus : []);
      } else {
        setMenus([]);
      }

      try {
        const remoteMenus = await fetchMenusFromApi();
        if (!isMounted) {
          return;
        }

        if (Array.isArray(remoteMenus) && remoteMenus.length > 0) {
          setMenus(remoteMenus);
          localStorage.setItem("carpeDiemMenus", JSON.stringify(remoteMenus));
        }
      } catch (_error) {
        // Keep local cache fallback.
      }
    };

    loadMenus();

    return () => {
      isMounted = false;
    };
  }, []);

  // Créer un nouveau menu vide
  const createNewMenu = () => {
    const newMenu = {
      id: `menu-${Date.now()}`,
      menuType: "normal",
      publishAsEvent: false,
      titre: "",
      soustitre: "",
      date: "",
      dateMenuJour: "",
      prix: [],
      image: "",
      sectionsOrder: [
        "entrees",
        "plats",
        "viandesPoissons",
        "fromages",
        "desserts",
        "boissons",
      ],
      entrees: [],
      plats: [],
      viandesPoissons: [],
      accompagnements: [],
      fromages: [],
      desserts: [],
      boissons: [],
      note: "",
      prixNote: [],
      noteFooter: "",
      titreEntrees: "Entrées",
      titrePlats: "Plats",
      titreViandesPoissons: "Viandes & Poissons",
      titreFromages: "Fromages",
      titreDesserts: "Desserts",
      titreBoissons: "Boissons",
    };
    setEditingMenu(newMenu);
    setShowForm(true);
  };

  // Créer un menu du jour préconfiguré.
  const createNewDailyMenu = () => {
    const todayDateKey = getTodayDateKey();
    const newMenu = {
      id: `menu-${Date.now()}`,
      menuType: "link",
      publishAsEvent: true,
      titre: "Menu du jour",
      soustitre: "",
      date: "",
      dateMenuJour: todayDateKey,
      prix: [],
      image: "",
      sectionsOrder: [
        "entrees",
        "plats",
        "viandesPoissons",
        "fromages",
        "desserts",
        "boissons",
      ],
      entrees: [],
      plats: [],
      viandesPoissons: [],
      accompagnements: [],
      fromages: [],
      desserts: [],
      boissons: [],
      note: "",
      prixNote: [],
      noteFooter: "",
      titreEntrees: "Entrées",
      titrePlats: "Plats",
      titreViandesPoissons: "Viandes & Poissons",
      titreFromages: "Fromages",
      titreDesserts: "Desserts",
      titreBoissons: "Boissons",
    };

    setEditingMenu(newMenu);
    setShowForm(true);
  };

  // Éditer un menu existant
  const editMenu = (menu) => {
    const menuWithDefaults = {
      ...menu,
      sectionsOrder: menu.sectionsOrder || [
        "entrees",
        "plats",
        "viandesPoissons",
        "fromages",
        "desserts",
        "boissons",
      ],
      titreEntrees: menu.titreEntrees || "Entrées",
      titrePlats: menu.titrePlats || "Plats",
      titreViandesPoissons: menu.titreViandesPoissons || "Viandes & Poissons",
      titreFromages: menu.titreFromages || "Fromages",
      titreDesserts: menu.titreDesserts || "Desserts",
      titreBoissons: menu.titreBoissons || "Boissons",
      boissons: menu.boissons || [],
      menuType: menu.menuType || "normal",
      dateMenuJour: menu.dateMenuJour || "",
      publishAsEvent: menu.publishAsEvent === true,
    };
    setEditingMenu({ ...menuWithDefaults });
    setShowForm(true);
  };

  const publishMenuAsDailyEvent = async (menu) => {
    const menuDateKey = normalizeDateKey(menu.dateMenuJour);
    const imageUrl = String(menu.image || "").trim();
    const title = String(menu.titre || "").trim() || "Menu du jour";
    const subtitle = String(menu.soustitre || "").trim();

    if (!menuDateKey) {
      return {
        success: false,
        error:
          "Renseignez une date du menu du jour au format AAAA-MM-JJ pour la popup.",
      };
    }

    if (!imageUrl) {
      return {
        success: false,
        error:
          "Une image est obligatoire pour publier le menu du jour en popup.",
      };
    }

    try {
      const events = await fetchEventsForAdmin();
      const eventId = buildDailyMenuEventId(menu.id);
      const descriptionParts = [subtitle, menu.date].filter((part) =>
        String(part || "").trim(),
      );

      const nextEvent = {
        id: eventId,
        title,
        description:
          descriptionParts.join(" - ") ||
          `Menu du jour du ${menuDateKey.split("-").reverse().join("/")}`,
        imageUrl,
        startDate: menuDateKey,
        endDate: menuDateKey,
        isActive: true,
        category: "daily-menu",
        linkedMenuId: menu.id,
      };

      const nextEvents = [
        ...events.filter((eventItem) => eventItem?.id !== eventId),
        nextEvent,
      ];

      await saveEventsToApi(nextEvents);
      window.dispatchEvent(new Event("carpe-content-changed"));
      return { success: true };
    } catch (_error) {
      return {
        success: false,
        error:
          "Le menu a été sauvegardé, mais la publication dans la popup événements a échoué.",
      };
    }
  };

  const removeDailyMenuEvent = async (menuId) => {
    try {
      const events = await fetchEventsForAdmin();
      const eventId = buildDailyMenuEventId(menuId);
      const nextEvents = events.filter(
        (eventItem) => eventItem?.id !== eventId,
      );
      await saveEventsToApi(nextEvents);
      window.dispatchEvent(new Event("carpe-content-changed"));
      return { success: true };
    } catch (_error) {
      return {
        success: false,
        error:
          "Le menu a été sauvegardé, mais la synchronisation avec la popup événements a échoué.",
      };
    }
  };

  // Supprimer un menu
  const deleteMenu = async (menuId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce menu ?")) {
      const updatedMenus = menus.filter((m) => m.id !== menuId);
      const result = await saveMenus(updatedMenus);
      if (!result.success) {
        alert(result.error);
        return;
      }

      const removeResult = await removeDailyMenuEvent(menuId);
      if (!removeResult.success) {
        alert(removeResult.error);
      }
    }
  };

  // Réordonner les menus (liste principale)
  const moveMenu = async (menuId, direction) => {
    const currentIndex = menus.findIndex((menu) => menu.id === menuId);
    if (currentIndex === -1) {
      return;
    }

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= menus.length) {
      return;
    }

    const reorderedMenus = [...menus];
    [reorderedMenus[currentIndex], reorderedMenus[targetIndex]] = [
      reorderedMenus[targetIndex],
      reorderedMenus[currentIndex],
    ];

    setIsSaving(true);
    const result = await saveMenus(reorderedMenus);
    setIsSaving(false);

    if (!result.success) {
      alert(result.error);
    }
  };

  // Sauvegarder le menu (création ou modification)
  const saveMenu = async () => {
    if (!editingMenu.titre) {
      alert("Le titre est obligatoire");
      return;
    }

    if (
      editingMenu.menuType === "link" &&
      !String(editingMenu.image || "").trim()
    ) {
      alert("Pour un menu lien, l'URL de l'image est obligatoire.");
      return;
    }

    if (
      editingMenu.publishAsEvent &&
      !normalizeDateKey(editingMenu.dateMenuJour)
    ) {
      alert(
        "Pour la popup événements, la date du menu du jour est obligatoire.",
      );
      return;
    }

    const existingIndex = menus.findIndex((m) => m.id === editingMenu.id);
    let updatedMenus;

    if (existingIndex !== -1) {
      updatedMenus = [...menus];
      updatedMenus[existingIndex] = editingMenu;
    } else {
      updatedMenus = [...menus, editingMenu];
    }

    setIsSaving(true);
    const result = await saveMenus(updatedMenus);

    if (!result.success) {
      setIsSaving(false);
      alert(result.error);
      return;
    }

    if (editingMenu.publishAsEvent) {
      const publishResult = await publishMenuAsDailyEvent(editingMenu);
      if (!publishResult.success) {
        setIsSaving(false);
        alert(publishResult.error);
        return;
      }
    } else {
      const removeResult = await removeDailyMenuEvent(editingMenu.id);
      if (!removeResult.success) {
        setIsSaving(false);
        alert(removeResult.error);
        return;
      }
    }

    setIsSaving(false);

    setShowForm(false);
    setEditingMenu(null);
  };

  // Annuler l'édition
  const cancelEdit = () => {
    setShowForm(false);
    setEditingMenu(null);
  };

  // Mettre à jour un champ simple
  const updateField = (field, value) => {
    setEditingMenu({ ...editingMenu, [field]: value });
  };

  // Ajouter un élément à un tableau
  const addArrayItem = (field, defaultValue = "") => {
    const currentArray = editingMenu[field] || [];
    setEditingMenu({
      ...editingMenu,
      [field]: [...currentArray, defaultValue],
    });
  };

  // Supprimer un élément d'un tableau
  const removeArrayItem = (field, index) => {
    const currentArray = editingMenu[field] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    setEditingMenu({ ...editingMenu, [field]: newArray });
  };

  // Mettre à jour un élément d'un tableau
  const updateArrayItem = (field, index, value) => {
    const currentArray = editingMenu[field] || [];
    const newArray = [...currentArray];
    newArray[index] = value;
    setEditingMenu({ ...editingMenu, [field]: newArray });
  };

  // Ajouter un prix
  const addPrix = () => {
    addArrayItem("prix", { label: "", montant: "" });
  };

  // Ajouter un prixNote
  const addPrixNote = () => {
    addArrayItem("prixNote", { label: "", montant: "" });
  };

  // Gérer le drag & drop des sections
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(editingMenu.sectionsOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setEditingMenu({
      ...editingMenu,
      sectionsOrder: items,
    });
  };

  // Obtenir le titre d'une section
  const getSectionTitle = (sectionKey) => {
    const titleMap = {
      entrees: editingMenu.titreEntrees || "Entrées",
      plats: editingMenu.titrePlats || "Plats",
      viandesPoissons: editingMenu.titreViandesPoissons || "Viandes & Poissons",
      fromages: editingMenu.titreFromages || "Fromages",
      desserts: editingMenu.titreDesserts || "Desserts",
      boissons: editingMenu.titreBoissons || "Boissons",
    };
    return titleMap[sectionKey] || sectionKey;
  };

  // Rendre une section selon son type
  const renderSection = (sectionKey) => {
    switch (sectionKey) {
      case "entrees":
        return (
          <div className="form-section">
            <h3>{editingMenu.titreEntrees || "Entrées"}</h3>
            {(editingMenu.entrees || []).map((entree, index) => (
              <div key={index} className="array-item">
                {typeof entree === "string" ? (
                  <input
                    type="text"
                    value={entree}
                    onChange={(e) =>
                      updateArrayItem("entrees", index, e.target.value)
                    }
                    placeholder="Ex: Salade de chèvre chaud ou OU"
                  />
                ) : (
                  <div className="complex-item">
                    <input
                      type="text"
                      placeholder="Nom du plat"
                      value={entree.nom || ""}
                      onChange={(e) =>
                        updateArrayItem("entrees", index, {
                          ...entree,
                          nom: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={entree.description || ""}
                      onChange={(e) =>
                        updateArrayItem("entrees", index, {
                          ...entree,
                          description: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Prix (Ex: 14,50€)"
                      value={entree.prix || ""}
                      onChange={(e) =>
                        updateArrayItem("entrees", index, {
                          ...entree,
                          prix: e.target.value,
                        })
                      }
                    />
                    <label>
                      <input
                        type="checkbox"
                        checked={entree.ou || false}
                        onChange={(e) =>
                          updateArrayItem("entrees", index, {
                            ...entree,
                            ou: e.target.checked,
                          })
                        }
                      />
                      Afficher "OU" après
                    </label>
                  </div>
                )}
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("entrees", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() =>
                addArrayItem("entrees", { nom: "", description: "", prix: "" })
              }
            >
              + Ajouter une entrée complexe
            </button>
            <button
              className="btn-add"
              onClick={() => addArrayItem("entrees", "")}
            >
              + Ajouter une entrée simple
            </button>
          </div>
        );

      case "plats":
        return (
          <div className="form-section">
            <h3>{editingMenu.titrePlats || "Plats"}</h3>
            {(editingMenu.plats || []).map((plat, index) => (
              <div key={index} className="array-item">
                {typeof plat === "string" ? (
                  <input
                    type="text"
                    value={plat}
                    onChange={(e) =>
                      updateArrayItem("plats", index, e.target.value)
                    }
                    placeholder="Ex: Magret de canard"
                  />
                ) : (
                  <div className="complex-item">
                    <input
                      type="text"
                      placeholder="Nom du plat"
                      value={plat.nom || ""}
                      onChange={(e) =>
                        updateArrayItem("plats", index, {
                          ...plat,
                          nom: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={plat.description || ""}
                      onChange={(e) =>
                        updateArrayItem("plats", index, {
                          ...plat,
                          description: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Prix"
                      value={plat.prix || ""}
                      onChange={(e) =>
                        updateArrayItem("plats", index, {
                          ...plat,
                          prix: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("plats", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() =>
                addArrayItem("plats", { nom: "", description: "", prix: "" })
              }
            >
              + Ajouter un plat complexe
            </button>
            <button
              className="btn-add"
              onClick={() => addArrayItem("plats", "")}
            >
              + Ajouter un plat simple
            </button>
          </div>
        );

      case "viandesPoissons":
        return (
          <div className="form-section">
            <h3>{editingMenu.titreViandesPoissons || "Viandes & Poissons"}</h3>
            {(editingMenu.viandesPoissons || []).map((item, index) => (
              <div key={index} className="array-item">
                <div className="complex-item">
                  <input
                    type="text"
                    placeholder="Nom du plat"
                    value={item.nom || ""}
                    onChange={(e) =>
                      updateArrayItem("viandesPoissons", index, {
                        ...item,
                        nom: e.target.value,
                      })
                    }
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={item.ou || false}
                      onChange={(e) =>
                        updateArrayItem("viandesPoissons", index, {
                          ...item,
                          ou: e.target.checked,
                        })
                      }
                    />
                    Afficher "OU" après
                  </label>
                </div>
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("viandesPoissons", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() =>
                addArrayItem("viandesPoissons", { nom: "", ou: false })
              }
            >
              + Ajouter un plat viande/poisson
            </button>
          </div>
        );

      case "fromages":
        return (
          <div className="form-section">
            <h3>{editingMenu.titreFromages || "Fromages"}</h3>
            {(editingMenu.fromages || []).map((item, index) => (
              <div key={index} className="array-item">
                <div className="complex-item">
                  <input
                    type="text"
                    placeholder="Nom du fromage"
                    value={item.nom || ""}
                    onChange={(e) =>
                      updateArrayItem("fromages", index, {
                        ...item,
                        nom: e.target.value,
                      })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Alternative (ex: ou Caillé Frais...)"
                    value={item.ou || ""}
                    onChange={(e) =>
                      updateArrayItem("fromages", index, {
                        ...item,
                        ou: e.target.value,
                      })
                    }
                  />
                </div>
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("fromages", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() => addArrayItem("fromages", { nom: "", ou: "" })}
            >
              + Ajouter un fromage
            </button>
          </div>
        );

      case "desserts":
        return (
          <div className="form-section">
            <h3>{editingMenu.titreDesserts || "Desserts"}</h3>
            {(editingMenu.desserts || []).map((dessert, index) => (
              <div key={index} className="array-item">
                {typeof dessert === "string" ? (
                  <input
                    type="text"
                    value={dessert}
                    onChange={(e) =>
                      updateArrayItem("desserts", index, e.target.value)
                    }
                    placeholder="Ex: Tarte aux pommes"
                  />
                ) : (
                  <div className="complex-item">
                    <input
                      type="text"
                      placeholder="Nom du dessert"
                      value={dessert.nom || ""}
                      onChange={(e) =>
                        updateArrayItem("desserts", index, {
                          ...dessert,
                          nom: e.target.value,
                        })
                      }
                    />
                    <label>
                      <input
                        type="checkbox"
                        checked={dessert.ou || false}
                        onChange={(e) =>
                          updateArrayItem("desserts", index, {
                            ...dessert,
                            ou: e.target.checked,
                          })
                        }
                      />
                      Afficher "OU" après
                    </label>
                  </div>
                )}
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("desserts", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() => addArrayItem("desserts", { nom: "", ou: false })}
            >
              + Ajouter un dessert complexe
            </button>
            <button
              className="btn-add"
              onClick={() => addArrayItem("desserts", "")}
            >
              + Ajouter un dessert simple
            </button>
          </div>
        );

      case "boissons":
        return (
          <div className="form-section">
            <h3>{editingMenu.titreBoissons || "Boissons"}</h3>
            {(editingMenu.boissons || []).map((boisson, index) => (
              <div key={index} className="array-item">
                {typeof boisson === "string" ? (
                  <input
                    type="text"
                    value={boisson}
                    onChange={(e) =>
                      updateArrayItem("boissons", index, e.target.value)
                    }
                    placeholder="Ex: Eau minérale"
                  />
                ) : (
                  <div className="complex-item">
                    <input
                      type="text"
                      placeholder="Nom de la boisson"
                      value={boisson.nom || ""}
                      onChange={(e) =>
                        updateArrayItem("boissons", index, {
                          ...boisson,
                          nom: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Description (ex: 25cl, Bouteille...)"
                      value={boisson.description || ""}
                      onChange={(e) =>
                        updateArrayItem("boissons", index, {
                          ...boisson,
                          description: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Prix"
                      value={boisson.prix || ""}
                      onChange={(e) =>
                        updateArrayItem("boissons", index, {
                          ...boisson,
                          prix: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <button
                  className="btn-remove"
                  onClick={() => removeArrayItem("boissons", index)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-add"
              onClick={() =>
                addArrayItem("boissons", { nom: "", description: "", prix: "" })
              }
            >
              + Ajouter une boisson complexe
            </button>
            <button
              className="btn-add"
              onClick={() => addArrayItem("boissons", "")}
            >
              + Ajouter une boisson simple
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="menu-admin-content">
      <div className="menu-admin-header">
        <div>
          <h1>Gestion des Menus</h1>
          <p className="page-subtitle">
            Créer et modifier les menus du restaurant
          </p>
        </div>
        <button className="btn-primary" onClick={createNewMenu}>
          + Créer un nouveau menu
        </button>
        <button className="btn-secondary" onClick={createNewDailyMenu}>
          + Créer un menu du jour (popup)
        </button>
      </div>

      {/* Liste des menus */}
      {!showForm && (
        <div className="menus-list">
          {menus.length === 0 ? (
            <div className="menu-card-admin">
              <div className="menu-card-header">
                <h3>Aucun menu enregistré</h3>
                <p className="subtitle">
                  Créez votre premier menu pour le publier sur le site.
                </p>
              </div>
            </div>
          ) : (
            menus.map((menu, index) => (
              <div key={menu.id} className="menu-card-admin">
                <div className="menu-card-header">
                  <h3>{menu.titre}</h3>
                  {menu.soustitre && (
                    <p className="subtitle">{menu.soustitre}</p>
                  )}
                </div>

                <div className="menu-card-order">
                  <span className="menu-order-label">
                    Position: {index + 1}
                  </span>
                  <div className="menu-order-actions">
                    <button
                      className="btn-order"
                      onClick={() => moveMenu(menu.id, -1)}
                      disabled={isSaving || index === 0}
                    >
                      ↑ Monter
                    </button>
                    <button
                      className="btn-order"
                      onClick={() => moveMenu(menu.id, 1)}
                      disabled={isSaving || index === menus.length - 1}
                    >
                      ↓ Descendre
                    </button>
                  </div>
                </div>

                <div className="menu-card-actions">
                  <button className="btn-edit" onClick={() => editMenu(menu)}>
                    Éditer
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => deleteMenu(menu.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Formulaire d'édition */}
      {showForm && editingMenu && (
        <div className="menu-form">
          <h2>
            {menus.find((m) => m.id === editingMenu.id)
              ? "Éditer le menu"
              : "Créer un menu"}
          </h2>

          {/* Informations principales */}
          <div className="form-section">
            <h3>Informations principales</h3>

            <div className="form-group">
              <label>Type de menu</label>
              <select
                value={editingMenu.menuType || "normal"}
                onChange={(e) => updateField("menuType", e.target.value)}
              >
                <option value="normal">
                  Menu normal (sections détaillées)
                </option>
                <option value="link">Menu lien (titre + date + image)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Titre *</label>
              <input
                type="text"
                value={editingMenu.titre}
                onChange={(e) => updateField("titre", e.target.value)}
                placeholder="Ex: Menu à 42,00€"
              />
            </div>

            <div className="form-group">
              <label>Sous-titre</label>
              <input
                type="text"
                value={editingMenu.soustitre || ""}
                onChange={(e) => updateField("soustitre", e.target.value)}
                placeholder="Ex: Menu du jour"
              />
            </div>

            <div className="form-group">
              <label>Date du menu du jour</label>
              <input
                type="date"
                value={editingMenu.dateMenuJour || ""}
                onChange={(e) => updateField("dateMenuJour", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="menu-switch">
                <input
                  type="checkbox"
                  checked={editingMenu.publishAsEvent === true}
                  onChange={(e) =>
                    updateField("publishAsEvent", e.target.checked)
                  }
                />
                Publier ce menu dans la popup événements
              </label>
              <p className="form-helper">
                La date du menu du jour sera utilisée comme date limite de
                suppression automatique.
              </p>
            </div>

            <div className="form-group">
              <label>Période / date libre</label>
              <input
                type="text"
                value={editingMenu.date || ""}
                onChange={(e) => updateField("date", e.target.value)}
                placeholder="Ex: Du Lundi au Vendredi"
              />
            </div>

            <div className="form-group">
              <label>URL de l'image</label>
              <input
                type="text"
                value={editingMenu.image || ""}
                onChange={(e) => updateField("image", e.target.value)}
                placeholder="https://... (image en grand)"
              />
              {editingMenu.image && (
                <img
                  src={editingMenu.image}
                  alt="Preview"
                  className="image-preview"
                />
              )}
            </div>
          </div>

          {/* Personnalisation des titres de sections */}
          {editingMenu.menuType !== "link" && (
            <div className="form-section">
              <h3>Personnaliser les titres des sections</h3>

              <div className="form-group">
                <label>Titre de la section Entrées</label>
                <input
                  type="text"
                  value={editingMenu.titreEntrees || "Entrées"}
                  onChange={(e) => updateField("titreEntrees", e.target.value)}
                  placeholder="Ex: Entrées, Hors d'œuvres, Starters..."
                />
              </div>

              <div className="form-group">
                <label>Titre de la section Plats</label>
                <input
                  type="text"
                  value={editingMenu.titrePlats || "Plats"}
                  onChange={(e) => updateField("titrePlats", e.target.value)}
                  placeholder="Ex: Plats, Plats principaux, Main courses..."
                />
              </div>

              <div className="form-group">
                <label>Titre de la section Viandes & Poissons</label>
                <input
                  type="text"
                  value={
                    editingMenu.titreViandesPoissons || "Viandes & Poissons"
                  }
                  onChange={(e) =>
                    updateField("titreViandesPoissons", e.target.value)
                  }
                  placeholder="Ex: Viandes & Poissons, Nos spécialités..."
                />
              </div>

              <div className="form-group">
                <label>Titre de la section Fromages</label>
                <input
                  type="text"
                  value={editingMenu.titreFromages || "Fromages"}
                  onChange={(e) => updateField("titreFromages", e.target.value)}
                  placeholder="Ex: Fromages, Plateau de fromages..."
                />
              </div>

              <div className="form-group">
                <label>Titre de la section Desserts</label>
                <input
                  type="text"
                  value={editingMenu.titreDesserts || "Desserts"}
                  onChange={(e) => updateField("titreDesserts", e.target.value)}
                  placeholder="Ex: Desserts, Douceurs, Sweets..."
                />
              </div>

              <div className="form-group">
                <label>Titre de la section Boissons</label>
                <input
                  type="text"
                  value={editingMenu.titreBoissons || "Boissons"}
                  onChange={(e) => updateField("titreBoissons", e.target.value)}
                  placeholder="Ex: Boissons, Carte des vins, Drinks..."
                />
              </div>
            </div>
          )}

          {/* Prix */}
          {editingMenu.menuType !== "link" && (
            <div className="form-section">
              <h3>Prix</h3>
              {(editingMenu.prix || []).map((prix, index) => (
                <div key={index} className="array-item">
                  <input
                    type="text"
                    placeholder="Label (Ex: Menu complet)"
                    value={prix.label}
                    onChange={(e) =>
                      updateArrayItem("prix", index, {
                        ...prix,
                        label: e.target.value,
                      })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Montant (Ex: 42,00 €)"
                    value={prix.montant}
                    onChange={(e) =>
                      updateArrayItem("prix", index, {
                        ...prix,
                        montant: e.target.value,
                      })
                    }
                  />
                  <button
                    className="btn-remove"
                    onClick={() => removeArrayItem("prix", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="btn-add" onClick={addPrix}>
                + Ajouter un prix
              </button>
            </div>
          )}

          {/* Sections réorganisables */}
          {editingMenu.menuType !== "link" && (
            <div className="sections-reorder">
              <h3 className="reorder-title">
                🔄 Réorganiser les sections (glisser-déposer)
              </h3>
              <p className="reorder-info">
                Faites glisser les sections pour changer leur ordre d'affichage
              </p>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sections">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="sections-droppable"
                    >
                      {editingMenu.sectionsOrder.map((sectionKey, index) => (
                        <Draggable
                          key={sectionKey}
                          draggableId={sectionKey}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`section-draggable ${
                                snapshot.isDragging ? "dragging" : ""
                              }`}
                            >
                              <div
                                {...provided.dragHandleProps}
                                className="drag-handle"
                              >
                                <span className="drag-icon">☰</span>
                                <span className="section-name">
                                  {getSectionTitle(sectionKey)}
                                </span>
                              </div>
                              {renderSection(sectionKey)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}

          {/* Notes */}
          {editingMenu.menuType !== "link" && (
            <div className="form-section">
              <h3>Notes et informations</h3>

              <div className="form-group">
                <label>Note</label>
                <textarea
                  value={editingMenu.note || ""}
                  onChange={(e) => updateField("note", e.target.value)}
                  placeholder="Ex: Du Lundi au Vendredi Midi Hors jours fériés"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Note de pied de page</label>
                <textarea
                  value={editingMenu.noteFooter || ""}
                  onChange={(e) => updateField("noteFooter", e.target.value)}
                  placeholder="Ex: Prix Nets, Taxes et services compris"
                  rows="2"
                />
              </div>

              <h4>Prix avec notes</h4>
              {(editingMenu.prixNote || []).map((prix, index) => (
                <div key={index} className="array-item">
                  <input
                    type="text"
                    placeholder="Label"
                    value={prix.label}
                    onChange={(e) =>
                      updateArrayItem("prixNote", index, {
                        ...prix,
                        label: e.target.value,
                      })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Montant"
                    value={prix.montant}
                    onChange={(e) =>
                      updateArrayItem("prixNote", index, {
                        ...prix,
                        montant: e.target.value,
                      })
                    }
                  />
                  <button
                    className="btn-remove"
                    onClick={() => removeArrayItem("prixNote", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="btn-add" onClick={addPrixNote}>
                + Ajouter un prix note
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="form-actions">
            <button className="btn-secondary" onClick={cancelEdit}>
              Annuler
            </button>
            <button
              className="btn-primary"
              onClick={saveMenu}
              disabled={isSaving}
            >
              {isSaving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuAdmin;
